import React from 'react';
import { PageSection, BlogSectionContent } from '../../types/page';
import { BlogGrid } from '../blog/BlogGrid';
import { Button } from '../ui/button';
import { useBlogPosts } from '../../hooks/useBlog';
import { ArrowRight, Calendar, Clock, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SectionSkeleton } from '../ui/Skeleton';
import { BlogGridSkeleton } from '../ui/Skeleton';

interface BlogSectionProps {
  section: PageSection;
  className?: string;
}

export const BlogSection: React.FC<BlogSectionProps> = ({ 
  section, 
  className = '' 
}) => {
  const content = section.content as BlogSectionContent;
  const { posts, loading, error } = useBlogPosts(content.featuredOnly);

  if (loading) {
    return (
      <SectionSkeleton title={section.title || 'Últimas Noticias'} subtitle={section.subtitle}>
        <BlogGridSkeleton count={content.limit || 3} />
      </SectionSkeleton>
    );
  }

  // Show empty state if there are no posts (graceful degradation)
  if (posts.length === 0) {
    return (
      <section className={`py-20 bg-gray-50 ${className}`}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              {section.title || 'Últimas Noticias'}
            </h2>
            {section.subtitle && (
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                {section.subtitle}
              </p>
            )}
          </div>
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">
              {error ? 'No se pudieron cargar los artículos del blog' : 'Próximamente encontrarás artículos interesantes aquí'}
            </p>
          </div>
        </div>
      </section>
    );
  }

  // Limit posts according to content configuration
  const displayPosts = posts.slice(0, content.limit || 3);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <section className={`py-20 bg-gray-50 ${className}`}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
            {section.title || 'Últimas Noticias'}
          </h2>
          {section.subtitle && (
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {section.subtitle}
            </p>
          )}
        </div>

        {/* Blog Posts Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {displayPosts.map((post) => (
            <article key={post.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group">
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                
                {/* Category Badge */}
                <div className="absolute top-3 left-3">
                  <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                    {post.category}
                  </span>
                </div>

                {post.featured && (
                  <div className="absolute top-3 right-3">
                    <span className="bg-red-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                      DESTACADO
                    </span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-lg font-bold mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                  <Link to={`/blog/${post.slug}`}>
                    {post.title}
                  </Link>
                </h3>

                <p className="text-gray-600 mb-4 line-clamp-3 text-sm">
                  {post.excerpt}
                </p>

                {/* Meta Information */}
                <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center">
                      <Calendar className="h-3 w-3 mr-1" />
                      <span>{formatDate(post.publishedAt)}</span>
                    </div>
                    <div className="flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      <span>{post.readTime} min</span>
                    </div>
                  </div>
                </div>

                {/* Author */}
                {post.author?.name && (
                  <div className="flex items-center mb-4">
                    {(post.author.image || (post.author as any).avatar) && (
                      <img
                        src={post.author.image || (post.author as any).avatar}
                        alt={post.author.name}
                        className="w-8 h-8 rounded-full mr-3 object-cover"
                      />
                    )}
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{post.author.name}</p>
                      {post.author.bio && (
                        <p className="text-gray-500 text-xs">{post.author.bio}</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="pt-2">
                  <Link
                    to={`/blog/${post.slug}`}
                    className="text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
                  >
                    Leer más →
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* View More Button */}
        {content.showViewMore && posts.length > displayPosts.length && (
          <div className="text-center">
            <Button 
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3"
              asChild
            >
              <Link to="/blog">
                Ver Más Artículos
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};